import React, { useCallback, useEffect, useState } from 'react';
import { Search, Plus, ChevronRight, Home, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

import { mccGet } from '../components/marketing/mccApi';

import BetaBadge from '../components/BetaBadge';
import MccSidebar from '../components/marketing/MccSidebar';
import MccDashboard from '../components/marketing/MccDashboard';
import MccMyWork from '../components/marketing/MccMyWork';
import MccIdeas from '../components/marketing/MccIdeas';
import MccRequests from '../components/marketing/MccRequests';
import MccFilming from '../components/marketing/MccFilming';
import MccFilmingCalendar from '../components/marketing/MccFilmingCalendar';
import MccRawFootage from '../components/marketing/MccRawFootage';
import MccWorkQueue from '../components/marketing/MccWorkQueue';
import MccPublishing from '../components/marketing/MccPublishing';
import MccAllPlatforms from '../components/marketing/MccAllPlatforms';
import MccHooks from '../components/marketing/MccHooks';
import MccCompleted from '../components/marketing/MccCompleted';
import MccTeamView from '../components/marketing/MccTeamView';
import MccNotifications from '../components/marketing/MccNotifications';
import MccSettings from '../components/marketing/MccSettings';
import MccCreateDialog from '../components/marketing/MccCreateDialog';
import MccProjectDrawer from '../components/marketing/MccProjectDrawer';

const CREATE_LABELS = {
  dashboard: 'Idea',
  'my-work': 'Idea',
  ideas: 'Idea',
  requests: 'Request',
  filming: 'Filming',
  'filming-calendar': 'Filming',
  'raw-footage': 'Project',
  'editing-queue': 'Project',
  reviews: 'Project',
  approvals: 'Project',
  publishing: 'Project',
  platforms: 'Project',
  hooks: 'Hook',
  completed: 'Project',
  'team-view': 'Project',
  notifications: 'Idea',
  settings: 'Project',
};

const CREATE_KIND = {
  Idea: 'idea',
  Request: 'request',
  Project: 'project',
  Filming: 'filming',
  Hook: 'hook',
};

export default function MarketingCommandCentre() {
  const [active, setActive] = useState(() => localStorage.getItem('mcc-active') || 'dashboard');
  const [collapsed, setCollapsed] = useState(false);
  const [team, setTeam] = useState([]);
  const [openProjectId, setOpenProjectId] = useState(null);
  const [openCreate, setOpenCreate] = useState({ open: false, kind: 'idea' });
  const [myWorkCount, setMyWorkCount] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const refreshBadges = useCallback(async () => {
    try {
      const [mw, n] = await Promise.all([
        mccGet('/marketing/my-work'),
        mccGet('/marketing/notifications'),
      ]);
      const c = mw.counts || {};
      setMyWorkCount((c.projects || 0) + (c.requests || 0) + (c.ideas || 0) + (c.filming || 0));
      setUnreadNotifs(n.unread || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const t = await mccGet('/marketing/team');
        setTeam(t.team || []);
      } catch (e) { toast.error(e.message || 'Failed to load team'); }
    })();
    refreshBadges();
    const int = setInterval(refreshBadges, 30000);
    return () => clearInterval(int);
  }, [refreshBadges]);

  useEffect(() => { localStorage.setItem('mcc-active', active); }, [active]);

  const openCreateFor = (label) => {
    setOpenCreate({ open: true, kind: CREATE_KIND[label] || 'idea' });
  };

  const openProject = (id) => { if (id) setOpenProjectId(id); };

  const runSearch = async (q) => {
    setSearch(q);
    if (!q.trim()) { setSearchResults(null); setSearchOpen(false); return; }
    try {
      const res = await mccGet('/marketing/search', { q });
      setSearchResults(res); setSearchOpen(true);
    } catch (e) { toast.error(e.message || 'Search failed'); }
  };

  const createLabel = CREATE_LABELS[active] || 'Idea';

  const renderSection = () => {
    switch (active) {
      case 'dashboard':
        return <MccDashboard team={team} onOpenProject={openProject} onNav={setActive} />;
      case 'my-work':
        return <MccMyWork team={team} onOpenProject={openProject} />;
      case 'ideas':
        return <MccIdeas team={team} onProjectCreated={(id) => { openProject(id); refreshBadges(); }} />;
      case 'requests':
        return <MccRequests team={team} onProjectCreated={(id) => { openProject(id); refreshBadges(); }} />;
      case 'filming':
        return <MccFilming team={team} onNav={setActive} />;
      case 'filming-calendar':
        return <MccFilmingCalendar team={team} />;
      case 'raw-footage':
        return <MccRawFootage team={team} onOpenProject={openProject} onNav={setActive} />;
      case 'editing-queue':
        return <MccWorkQueue team={team} mode="editing" onOpenProject={openProject} onNav={setActive} />;
      case 'reviews':
        return <MccWorkQueue team={team} mode="reviews" onOpenProject={openProject} onNav={setActive} />;
      case 'approvals':
        return <MccWorkQueue team={team} mode="approvals" onOpenProject={openProject} onNav={setActive} />;
      case 'publishing':
        return <MccPublishing onOpenProject={openProject} onNav={setActive} />;
      case 'platforms':
        return <MccAllPlatforms team={team} onOpenProject={openProject} />;
      case 'hooks':
        return <MccHooks team={team} />;
      case 'completed':
        return <MccCompleted team={team} onOpenProject={openProject} />;
      case 'team-view':
        return <MccTeamView team={team} onOpenProject={openProject} />;
      case 'notifications':
        return <MccNotifications onNav={setActive} onOpenProject={openProject} />;
      case 'settings':
        return <MccSettings />;
      default:
        return <MccDashboard team={team} onOpenProject={openProject} onNav={setActive} />;
    }
  };

  return (
    <div className="h-screen flex bg-slate-100 overflow-hidden">
      <MccSidebar
        active={active}
        onSelect={(v) => { setActive(v); setSearchOpen(false); }}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        myWorkCount={myWorkCount}
        unreadNotifs={unreadNotifs}
      />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-shrink-0">
          <Link
            to="/"
            className="flex items-center gap-2 text-slate-900 hover:text-indigo-600 transition select-none"
            title="Back to Derwent AI home"
            data-testid="mcc-home-logo"
          >
            <Home className="w-4 h-4" />
            <span className="text-sm font-black tracking-tight">DERWENT<span className="text-indigo-600">AI</span></span>
          </Link>
          <span className="text-xs font-black text-slate-400 hidden sm:inline">·</span>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[11.5px] font-bold text-slate-700">
            Marketing Command Centre
            <BetaBadge size="xs" />
          </span>
          {localStorage.getItem('mcc-demo-mode') === 'on' && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800"
              title="You are inside the demo account. Toggle off in Settings to return to your live account."
              data-testid="mcc-demo-badge"
            >
              <Sparkles className="w-3 h-3" /> Demo Mode
            </span>
          )}
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => runSearch(e.target.value)}
              onFocus={() => search && setSearchOpen(true)}
              placeholder="Search projects, ideas, hooks, requests, filming…"
              className="pl-8 h-9"
              data-testid="mcc-global-search"
            />
            {searchOpen && searchResults && (
              <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg max-h-[420px] overflow-y-auto z-40" onMouseLeave={() => setSearchOpen(false)}>
                {['projects', 'ideas', 'requests', 'hooks', 'filming'].map((k) => {
                  const list = searchResults[k] || [];
                  if (!list.length) return null;
                  return (
                    <div key={k}>
                      <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 sticky top-0">{k}</p>
                      {list.map((it, idx) => (
                        <button key={`${k}-${idx}`}
                          onClick={() => {
                            setSearchOpen(false); setSearch('');
                            if (k === 'projects' && it.id) openProject(it.id);
                            else if (k === 'ideas') setActive('ideas');
                            else if (k === 'requests') setActive('requests');
                            else if (k === 'hooks') setActive('hooks');
                            else if (k === 'filming') setActive('filming');
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center gap-2"
                        >
                          <span className="text-sm text-slate-800 truncate flex-1">
                            {it.title || it.hook || it.person_being_filmed || '—'}
                          </span>
                          <ChevronRight className="w-3 h-3 text-slate-400" />
                        </button>
                      ))}
                    </div>
                  );
                })}
                {['projects', 'ideas', 'requests', 'hooks', 'filming'].every((k) => (searchResults[k] || []).length === 0) && (
                  <p className="p-4 text-center text-sm text-slate-500">No matches for &ldquo;{search}&rdquo;.</p>
                )}
              </div>
            )}
          </div>
          <Button
            onClick={() => openCreateFor(createLabel)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="mcc-topbar-create"
          >
            <Plus className="w-4 h-4 mr-1" /> New {createLabel}
          </Button>
        </header>

        <main className="flex-1 overflow-y-auto bg-slate-100" data-testid={`mcc-section-${active}`}>
          {renderSection()}
        </main>
      </div>

      {openProjectId && (
        <MccProjectDrawer
          projectId={openProjectId}
          team={team}
          onClose={() => setOpenProjectId(null)}
          onChanged={refreshBadges}
        />
      )}

      <MccCreateDialog
        open={openCreate.open}
        onClose={() => setOpenCreate({ open: false, kind: 'idea' })}
        kind={openCreate.kind}
        team={team}
        onCreated={(res) => {
          refreshBadges();
          if (openCreate.kind === 'project' && res?.id) openProject(res.id);
        }}
      />
    </div>
  );
}
